import { useLocation } from 'react-router-dom'
import { useGetTeamLeagueId } from '@/api/generated/team-league/team-league'
import { useGetLeagueId } from '@/api/generated/league/league'
import { useGetPlayerIdView } from '@/api/generated/player-player/player-player'
import { usePageTitle } from '@/features/_shared/usePageTitle'
import { tabSuffix, staticTitle } from '@/features/_shared/pageTitle'

// Single owner of document.title for the whole app. Rendered once in Layout (which
// persists across navigations), it reads the current path and sets a descriptive
// title so browser history + Chrome's address-bar search can suggest the actual
// page ("Gas House Gorillas", "SP John Smith") instead of every page being
// "BaseHit". Entity names come from the SAME identity queries the secondary nav
// already runs, so this adds no network cost on those pages. Game/help pages set
// their own (richer) titles; for those PageTitle renders nothing and doesn't touch
// document.title — never two owners at once.

function TeamTitle({ teamId, seg }: { teamId: number; seg?: string }) {
  const { data } = useGetTeamLeagueId(teamId, { query: { enabled: !Number.isNaN(teamId) } })
  usePageTitle(data?.fullName ? `${data.fullName}${tabSuffix(seg)}` : undefined)
  return null
}

function LeagueTitle({ leagueId, seg }: { leagueId: number; seg?: string }) {
  const { data } = useGetLeagueId(leagueId, { query: { enabled: !Number.isNaN(leagueId) } })
  usePageTitle(data?.name ? `${data.name}${tabSuffix(seg)}` : undefined)
  return null
}

function PlayerTitle({ playerId, seg }: { playerId: number; seg?: string }) {
  const { data } = useGetPlayerIdView(playerId, { query: { enabled: !Number.isNaN(playerId) } })
  usePageTitle(data?.fullName ? `${data.fullName}${tabSuffix(seg)}` : undefined)
  return null
}

function StaticTitle({ title }: { title: string }) {
  usePageTitle(title)
  return null
}

export function PageTitle() {
  const { pathname } = useLocation()
  const seg = pathname.split('/').filter(Boolean)

  if (seg[0] === 'team' && seg[1]) return <TeamTitle teamId={Number(seg[1])} seg={seg[2]} />
  if (seg[0] === 'league' && seg[1]) return <LeagueTitle leagueId={Number(seg[1])} seg={seg[2]} />
  if (seg[0] === 'player' && seg[1]) return <PlayerTitle playerId={Number(seg[1])} seg={seg[2]} />

  const title = staticTitle(pathname)
  if (title !== undefined) return <StaticTitle title={title} />

  // Not a PageTitle-owned route (game/*, help/*, 404) — leave document.title to
  // that page's own mechanism (or the default).
  return null
}
